import React, {
  createContext,
  type ReactNode,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { auth, db } from '../api/firebase/config';
import { getUser } from '../api/firebase/firestore/user/read';
import { getCompany } from '../api/firebase/firestore/company/read';
import { UserSetType } from '../types/user';
import * as Sentry from '@sentry/react';
import { pushUserParams } from '../gtm/usePageTracking';

// 会社情報の型（OCR上限計算に必要な情報）
export type CompanyContextType = {
  company_id: string;
  company_name: string;
  plan: number;
  ocr_monthly_budget: number;
  ocr_credit_balance: number;
  current_pattern_count: number;
  current_user_count: number;
  user_limit: number;
  enable_master: boolean;
  /** 企業ユーザー自身によるマスタCSVアップロードの可否（false なら運営のみアップロード可） */
  master_self_upload: boolean;
  /** 辞書変換機能の有効/無効フラグ（運営が企業単位で有効化） */
  enable_dictionary: boolean;
  /** 指定フォーマット出力の有効/無効（#3070。運営が企業単位で有効化） */
  enable_export_template: boolean;
  pattern_limit: number;
  /**
   * AIマスタ照合レコード数の上限（#3288）。
   * 未設定（本機能より前から存在する企業）は制限なし扱いのため既定値で埋めない。
   */
  master_record_limit?: number;
  webhook_enabled?: boolean;
  /** 子パターンの有効/無効（#2967。運営が企業単位で有効化） */
};

type AuthContextType = {
  user: UserSetType | null;
  company: CompanyContextType | null;
  isLoading: boolean;
  setCurrentUser: (user: UserSetType | null) => void;
  setCurrentCompany: (company: CompanyContextType | null) => void;
  /**
   * ログイン中のユーザー自身の企業情報を取り直す（#3326）。
   * 代理ログインのように外部から company を差し替える処理が、元の状態へ戻すために使う。
   * リロードしないと復旧しない状態を作らないため、購読もあわせて張り直す。
   */
  refreshCompany: () => void;
};

// デフォルト値にnoop関数を使用
const noop = (): void => void {};

export const AuthContext = createContext<AuthContextType>({
  user: null as UserSetType | null,
  company: null as CompanyContextType | null,
  isLoading: true,
  setCurrentUser: noop,
  setCurrentCompany: noop,
  refreshCompany: noop,
});

// Firestore の companies ドキュメントを AuthContext が持つ形へ変換する。
// 初回取得・スナップショット更新・再取得のいずれからも同じ形になるよう1か所に集約する（#3326）。
const toCompanyContext = (companyId: string, data: DocumentData): CompanyContextType => ({
  company_id: companyId,
  company_name: (data.company_name as string) ?? '',
  plan: (data.plan as number) ?? 0,
  ocr_monthly_budget: (data.ocr_monthly_budget as number) ?? 0,
  ocr_credit_balance: (data.ocr_credit_balance as number) ?? 0,
  current_pattern_count: (data.current_pattern_count as number) ?? 0,
  current_user_count: (data.current_user_count as number) ?? 0,
  user_limit: (data.user_limit as number) ?? 0,
  enable_master: (data.enable_master as boolean) ?? false,
  master_self_upload: (data.master_self_upload as boolean) ?? false,
  enable_dictionary: (data.enable_dictionary as boolean) ?? false,
  enable_export_template: (data.enable_export_template as boolean) ?? false,
  pattern_limit: (data.pattern_limit as number) ?? 1,
  master_record_limit: data.master_record_limit as number | undefined,
  webhook_enabled: (data.webhook_enabled as boolean) === true,
});

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<UserSetType | null>(null);
  const [company, setCompany] = useState<CompanyContextType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const companyUnsubscribeRef = useRef<(() => void) | null>(null);
  // ログイン中のユーザー自身の企業ID（#3326）。
  // 代理ログイン終了後に「誰の企業情報へ戻すか」を知るため、company state とは別に保持する
  // （company は代理中に他社のもので上書きされ、終了時には null にされるため頼れない）。
  const ownCompanyIdRef = useRef<string | null>(null);

  // 指定した企業のドキュメントを購読し、AuthContext.company へ反映する。
  // 前の購読は必ず解除してから張り直す。
  const subscribeCompany = useCallback((companyId: string) => {
    if (companyUnsubscribeRef.current) {
      companyUnsubscribeRef.current();
      companyUnsubscribeRef.current = null;
    }

    const companyDocRef = doc(db, 'companies', companyId);
    companyUnsubscribeRef.current = onSnapshot(
      companyDocRef,
      snapshot => {
        if (snapshot.exists()) {
          setCompany(toCompanyContext(companyId, snapshot.data()));
        }
      },
      snapshotError => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (snapshotError instanceof FirebaseError && snapshotError.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(snapshotError);
        setCompany(null);
      },
    );
  }, []);

  // ユーザー自身の企業情報を取り直す（#3326）。
  // 代理ログイン終了時など、外部から company を差し替えたあとの復旧に使う。
  const refreshCompany = useCallback(() => {
    const companyId = ownCompanyIdRef.current;
    if (!companyId) {
      setCompany(null);
      return;
    }
    // onSnapshot は初回接続時にもコールバックが発火するため、別途の初期取得は不要
    subscribeCompany(companyId);
  }, [subscribeCompany]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async authUser => {
        // 前のcompanyリスナーをクリーンアップ
        if (companyUnsubscribeRef.current) {
          companyUnsubscribeRef.current();
          companyUnsubscribeRef.current = null;
        }
        // 認証状態が変わった時点で持ち主が変わるため、いったん破棄してから取得し直す。
        // 残したままだと、ログアウト後の refreshCompany が前のユーザーの企業を購読しかねない。
        ownCompanyIdRef.current = null;

        if (authUser) {
          try {
            const userData = await getUser(authUser.uid);
            const companyData = await getCompany(userData?.company_id);

            // GTMにデータを流す（UserSetTypeに変換する前）
            pushUserParams(
              userData ? { ...userData, role: userData.role ?? undefined } : undefined,
              companyData,
              authUser.uid,
            );

            if (userData) {
              const newUserData = {
                uid: authUser.uid,
                name: userData.name,
                icon: userData.icon_url,
                role: userData.role,
                company_id: userData.company_id,
                group_ids: userData.group_ids ?? [], // 所属グループ（#2804 アクセス制御用）
              };
              setUser(newUserData);
              ownCompanyIdRef.current = userData.company_id ?? null;

              // 会社情報をリアルタイム監視（OCRクレジット残高の変更を即座に反映）
              if (companyData && userData.company_id) {
                // 初期値を設定
                setCompany(toCompanyContext(userData.company_id, companyData));
                subscribeCompany(userData.company_id);
              } else {
                setCompany(null);
              }

              Sentry.setUser({
                email: authUser?.email ?? undefined,
                id: authUser?.uid ?? undefined,
                company_id: userData?.company_id,
                company_name: companyData?.company_name,
              });
              setIsLoading(false);
            } else {
              setUser(null);
              setCompany(null);
              setIsLoading(false);
            }
          } catch (authError) {
            Sentry.captureException(authError);
            setUser(null);
            setCompany(null);
            setIsLoading(false);
          }
        } else {
          // --- ログアウト（未ログイン）時もGTMに通知を送る ---
          pushUserParams(null, null, undefined);
          setUser(null);
          setCompany(null);
          setIsLoading(false);
        }
      },
      authError => {
        Sentry.captureException(authError);
        setUser(null);
        setCompany(null);
        ownCompanyIdRef.current = null;
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
      // companyリスナーもクリーンアップ
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
      }
    };
  }, [subscribeCompany]);

  const setCurrentUser = useCallback((newUser: UserSetType | null) => {
    setUser(newUser);
    setIsLoading(false);
  }, []);

  const setCurrentCompany = useCallback((newCompany: CompanyContextType | null) => {
    setCompany(newCompany);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, company, isLoading, setCurrentUser, setCurrentCompany, refreshCompany }}
    >
      {children}
    </AuthContext.Provider>
  );
};
